<template>
  <GamMainView class="players-view">
    <GamDetailView :list-id="GamListId.PLAYERS" is-open>
      <ScrollOverflow v-if="activeUsername" ref="detailScrollRef" class="detail-scroll" is-vertical>
        <PlayerDetail :username="activeUsername" :key="`player-id-${activeUsername}-${lastRefreshedAt}`" is-detail-view>
          <template #pull-to-refresh>
            <Vue3PullToRefresh
              :distance="50"
              :duration="300"
              :size="32"
              noreload
              :options="{ color: '#CCC', bgColor: '#282828' }"
              @onrefresh="handleRefreshDetails"
            />
          </template>
        </PlayerDetail>
      </ScrollOverflow>
    </GamDetailView>

    <ScreenNav v-if="isOwnProfile" active="profile" @profile="goToProfile" @map="goToMap" @sparks="goToSparks">
      <template #right>
        <div :class="['nav-side-btn nav-news-btn', { 'has-unread': unreadCount > 0 }]" @click="handleNotificationClick">
          <GamIcon :name="GamIconName.NOTIFICATIONS_EMPTY" :size="GamIconSize.LARGE" is-current />
          <div v-if="unreadCount" class="news-badge">
            <GamChip v-bind="getNewsBadgeChip" />
          </div>
        </div>
      </template>
    </ScreenNav>

    <div v-else class="nav-close-btn" @click="handleClose">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
    </div>
  </GamMainView>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { UserDto } from '@/core/data/user/user.interface';
import { gambits } from '@/core/gambits';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamMainView from '@/views/components/GamMainView.vue';
import ScreenNav from '@/views/components/ScreenNav.vue';
import ScrollOverflow from '@/views/components/ScrollOverflow.vue';
import GamDetailView from '@/views/components/detail-view/GamDetailView.vue';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
// @ts-ignore
import Vue3PullToRefresh from '@amirafa/vue3-pull-to-refresh';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PlayerDetail from './PlayerDetail.vue';

const playerDetails = ref<UserDto | null>(null);
const lastRefreshedAt = ref<number>(new Date().getTime());
const isMounted = ref<boolean>(false);
const detailScrollRef = ref<InstanceType<typeof ScrollOverflow>>();

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);

const notificationStore = useNotificationStore();
const { unreadCount } = storeToRefs(notificationStore);

const appStore = useAppStore();

const router = useRouter();
const route = useRoute();

const activeUsername = ref<string | null>(route.params.username ? String(route.params.username) : null);

watch(
  () => route.params.username,
  (username) => {
    if (username) {
      activeUsername.value = String(username);
    }
  },
);

if (appStore.isFirstVisit() && window.location.search.includes('back')) {
  router.replace({ ...route, query: { ...route.query, back: undefined } });
}

onMounted(async () => {
  isMounted.value = true;
  getPlayerDetail();
});

onBeforeUnmount(() => {
  isMounted.value = false;
});

watchEffect(() => {
  if (!isMounted.value) {
    // navStore.setPlayerView(false);
  } else {
    if (!user.value) {
      // navStore.setPlayerView(true);
    } else {
      if (playerDetails.value?.id === user.value?.id) {
        // navStore.setPlayerView(false);
        // navStore.setProfileView(true);
      } else {
        // navStore.setPlayerView(true);
        // navStore.setProfileView(false);
      }
    }
  }
});

const handleRefreshDetails = () => {
  lastRefreshedAt.value = new Date().getTime();
};

const getPlayerDetail = async () => {
  const username = route.params.username;

  if (!username) return null;

  playerDetails.value = null;
  const result = await gambits.userService.getUserByName(String(username));

  if (!result.data) return null;

  playerDetails.value = result.data;
};

const isOwnProfile = computed(
  () => !!userDetails.value?.username && activeUsername.value === userDetails.value.username,
);

const { absorb: absorbGhostClick } = useGhostClickGuard();

/* The nav only shows on your own profile, so its profile CTA is already the
   active tab — tapping it returns to the top of the page instead. */
const goToProfile = () => {
  absorbGhostClick();
  detailScrollRef.value?.resetScroll();
};

const handleClose = () => {
  absorbGhostClick();
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push({ name: RouterNameType.LOCATIONS });
  }
};

const goToMap = () => {
  absorbGhostClick();
  router.push({ name: RouterNameType.LOCATIONS });
};

const goToSparks = () => {
  absorbGhostClick();
  router.push({ name: RouterNameType.SPARKS });
};

const handleNotificationClick = () => {
  absorbGhostClick();
  router.push({ name: RouterNameType.NEWS, query: { back: 'history' } });
};

const getNewsBadgeChip = computed((): GamChipType | undefined => {
  if (unreadCount.value) {
    return {
      label: Math.min(unreadCount.value, 99).toString(),
      variant: GamChipVariant.PRIMARY,
      size: GamChipSize.X_SMALL,
      isActive: true,
    };
  }
  return undefined;
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.ScreenNav {
  --screen-nav-z: 1000;
}

.nav-close-btn {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.nav-side-btn {
  position: relative;
  width: 3rem;
  height: 3rem;
  border-radius: 3rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}

.nav-news-btn {
  &.has-unread {
    background: var(--color-pink-20);
    border-color: var(--color-dark-900);
    color: var(--color-white);
  }
  @include hover-capable {
    &:hover:not(.has-unread) {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }
  }

  .news-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    pointer-events: none;
  }
}

.detail-scroll {
  width: 100%;
  height: 100svh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(175deg, oklch(0.28 0.035 var(--hue)) 0%, var(--color-dark-700) 45%), var(--color-dark-700);
}

.player-tab-selector {
  display: flex;
  justify-self: center;
  margin: 0 auto;
  padding: var(--spacing-xxl) 0;
  flex-grow: 1;
  justify-content: flex-end;

  .tab-container {
    display: flex;
    flex-direction: row;
    gap: 24px;
  }
}

.metrics {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  flex-direction: column;
  // gap: var(--spacing-md);
  gap: var(--spacing-md);
}

.details-cards {
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: var(--spacing-md);
}

.right-side-panel,
.gam-form-content {
  width: 100%;
  height: calc(100% - 5rem);
}
</style>

<style lang="scss">
.main-view.players-view .list-content {
  gap: var(--spacing-md) !important;
}
</style>
